import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal,
    Form,
    Input,
    DatePicker,
    InputNumber,
    Select,
    Tabs,
    Table,
    message,
    Progress,
    Tag,
    Statistic,
    Row,
    Col,
    Card,
    Typography,
    Button,
    Space,
    Divider,
} from 'antd';
import { 
    PlusOutlined,
    UserAddOutlined,
    UserDeleteOutlined,
    EditOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import BudgetPlanningTab from '../../components/BudgetPlanningTab';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ProjectEditModal = ({ visible, onCancel, onSuccess, projectId }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('basic');
    const [projectData, setProjectData] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]); // 전체 사용자 (PM/기술리더용)
    const [projectUsers, setProjectUsers] = useState([]); // 프로젝트 배정 가능한 사용자
    const [allocations, setAllocations] = useState([]);
    const [budgets, setBudgets] = useState([]);

    // ⭐ 추가
    const [allocationEditing, setAllocationEditing] = useState(false);
    const [editableAllocations, setEditableAllocations] = useState([]);
    // ⭐ 예산 편성 관련 state 추가
    const [budgetEditing, setBudgetEditing] = useState(false);
    const [editableBudgets, setEditableBudgets] = useState([]);
    
    // ⭐ 인력 배정 관련 state 추가
    const [memberEditing, setMemberEditing] = useState(false);
    const [memberModalVisible, setMemberModalVisible] = useState(false);
    const [memberForm] = Form.useForm();
    const [departmentBudgets, setDepartmentBudgets] = useState({}); // 부서별 예산 정보
    const [estimatedLaborCost, setEstimatedLaborCost] = useState(0); // 예상 인건비

    const loadProjectData = useCallback(async () => {
        if (!projectId) return;

        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/projects/${projectId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const project = response.data;
            console.log('📊 Project Data:', project);

            setProjectData(project);


            form.setFieldsValue({
                ...project,
                contract_date: project.contract_date ? dayjs(project.contract_date) : null,
                start_date: project.start_date ? dayjs(project.start_date) : null,
                end_date: project.end_date ? dayjs(project.end_date) : null,
                pm_id: project.pm_id,
                tech_leader_id: project.tech_leader_id,
                primary_department_id: project.primary_department_id,
            });

            // ⭐ allocations 처리 - department 객체 포함
            const allocs = project.allocations || [];
            setAllocations(allocs);
            // 편집 모드가 아닐 때만 editableAllocations 초기화
            if (!allocationEditing) {
                setEditableAllocations(allocs.map(a => ({
                    id: a.id,
                    department_id: a.department_id,
                    department: a.department,  // ⭐ 객체 전체 저장
                    allocation_rate: a.allocation_rate,
                    allocated_amount: a.allocated_amount
                })));
            }

            // ⭐ budgets 처리
            const budgs = project.budgets || [];
            setBudgets(budgs);
            // 편집 모드가 아닐 때만 editableBudgets 초기화
            if (!budgetEditing) {
                setEditableBudgets(budgs.map(b => ({
                    id: b.id,
                    department_id: b.department_id,
                    department: b.department,
                    revenue_budget: b.revenue_budget || 0,
                    labor_budget: b.labor_budget || 0,
                    outsourcing_budget: b.outsourcing_budget || 0,
                    expense_budget: b.expense_budget || 0,
                    other_budget: b.other_budget || 0
                })));
            }

        } catch (error) {
            message.error('프로젝트 정보를 불러오는데 실패했습니다');
            console.error('Load project error:', error);
        }
    }, [projectId, form]);

    const loadDepartments = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE}/departments`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            console.log('🔍 Departments response:', response.data);

            // ⭐ 응답 구조에 따라 처리
            const depts = Array.isArray(response.data)
                ? response.data
                : response.data.departments || [];

            setDepartments(depts.filter(d => d.code !== 'UNASSIGNED'));
            
            // 부서별 예산 정보도 로드
            await loadDepartmentBudgets(depts.filter(d => d.code !== 'UNASSIGNED'));
        } catch (error) {
            console.error('Load departments error:', error);
            message.error('부서 목록을 불러오는데 실패했습니다');
        }
    }, []);

    const loadUsers = useCallback(async (departmentId = null) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE}/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // 전체 사용자 로드
            let allUsers = Array.isArray(response.data) ? response.data : response.data.users || [];
            
            // 부서 필터링 (제공된 경우)
            if (departmentId) {
                allUsers = allUsers.filter(user => user.department_id === departmentId);
            }
            
            setUsers(allUsers);
        } catch (error) {
            console.error('Load users error:', error);
        }
    }, []);

    const loadProjectUsers = useCallback(async () => {
        if (!projectData?.allocations) return;
        
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE}/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // 프로젝트 매출 귀속 부서의 부서원만 필터링
            const allUsers = Array.isArray(response.data) ? response.data : response.data.users || [];
            const allocatedDepartmentIds = projectData.allocations.map(alloc => alloc.department_id);
            const filteredUsers = allUsers.filter(user => 
                user.department_id && allocatedDepartmentIds.includes(user.department_id)
            );
            
            setProjectUsers(filteredUsers);
        } catch (error) {
            console.error('Load project users error:', error);
        }
    }, [projectData?.allocations]);

    useEffect(() => {
        if (visible && projectId) {
            loadProjectData();
            loadDepartments();
        }
    }, [visible, projectId, loadProjectData, loadDepartments]);

    // 프로젝트 데이터가 로드된 후 대표부서에 따라 사용자 로드
    useEffect(() => {
        if (projectData?.primary_department_id) {
            loadUsers(projectData.primary_department_id);
        }
    }, [projectData?.primary_department_id, loadUsers]);

    // 프로젝트 데이터가 로드된 후 프로젝트 배정 가능한 사용자 목록 로드
    useEffect(() => {
        if (projectData?.allocations) {
            loadProjectUsers();
        }
    }, [projectData?.allocations, loadProjectUsers]);

    // 부서별 예산 정보 로드
    const loadDepartmentBudgets = useCallback(async (departments) => {
        try {
            const token = localStorage.getItem('token');
            const currentYear = new Date().getFullYear();
            const budgetData = {};
            
            for (const dept of departments) {
                try {
                    const response = await axios.get(`${API_BASE}/departments/${dept.id}/budgets?year=${currentYear}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    
                    const budgets = response.data.budgets || [];
                    const currentBudget = budgets.find(b => b.year === currentYear);
                    
                    if (currentBudget) {
                        // 1인당 1일 평균 인건비 계산
                        const dailyLaborCost = currentBudget.labor_budget && currentBudget.employee_count 
                            ? currentBudget.labor_budget / currentBudget.employee_count / 365
                            : 0;
                            
                        budgetData[dept.id] = {
                            ...currentBudget,
                            dailyLaborCostPerPerson: dailyLaborCost
                        };
                    }
                } catch (error) {
                    console.warn(`부서 ${dept.name} 예산 정보 로드 실패:`, error);
                    budgetData[dept.id] = {
                        dailyLaborCostPerPerson: 0
                    };
                }
            }
            
            setDepartmentBudgets(budgetData);
            console.log('부서별 예산 정보:', budgetData);
        } catch (error) {
            console.error('부서별 예산 정보 로드 실패:', error);
        }
    }, []);

    // 예상 인건비 계산
    const calculateEstimatedLaborCost = useCallback((departmentId, personCount, workDays, participationRate) => {
        const budget = departmentBudgets[departmentId];
        if (!budget || !budget.dailyLaborCostPerPerson) {
            return 0;
        }
        
        const estimatedCost = personCount * workDays * (participationRate / 100) * budget.dailyLaborCostPerPerson;
        return Math.round(estimatedCost);
    }, [departmentBudgets]);

    // 폼 값 변경 시 예상 인건비 업데이트
    const updateEstimatedCost = useCallback(() => {
        const values = memberForm.getFieldsValue();
        const { budget_department_id, person_count, work_days, participation_rate } = values;
        
        if (budget_department_id && person_count && work_days && participation_rate) {
            const cost = calculateEstimatedLaborCost(
                budget_department_id, 
                person_count, 
                work_days, 
                participation_rate
            );
            setEstimatedLaborCost(cost);
        } else {
            setEstimatedLaborCost(0);
        }
    }, [memberForm, calculateEstimatedLaborCost]);

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            const token = localStorage.getItem('token');

            const data = {
                ...values,
                contract_date: values.contract_date?.format('YYYY-MM-DD'),
                start_date: values.start_date?.format('YYYY-MM-DD'),
                end_date: values.end_date?.format('YYYY-MM-DD'),
                department_allocations: allocations.map(a => ({  // ⭐ 추가
                    id: a.id,
                    department_id: a.department_id,
                    allocation_rate: a.allocation_rate,
                    allocated_amount: a.allocated_amount
                })),
                budgets: budgets.length > 0 ? budgets.map(b => ({  // ⭐ 저장된 budgets 사용
                    id: b.id,
                    department_id: b.department_id,
                    revenue_budget: Math.round(b.revenue_budget || 0),
                    labor_budget: Math.round(b.labor_budget || 0),
                    outsourcing_budget: Math.round(b.outsourcing_budget || 0),
                    expense_budget: Math.round(b.expense_budget || 0),
                    other_budget: Math.round(b.other_budget || 0)
                })) : []
            };

            console.log('=== 프로젝트 수정 데이터 ===');
            console.log('Primary Department ID:', data.primary_department_id);
            console.log('Department Allocations:', data.department_allocations);
            console.log('Full data:', data);

            await axios.put(
                `${API_BASE}/projects/${projectId}`,
                data,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            message.success('프로젝트가 수정되었습니다');

            form.resetFields();
            setActiveTab('basic');
            setProjectData(null);

            if (onSuccess) onSuccess();
            if (onCancel) onCancel();

        } catch (error) {
            message.error(error.response?.data?.message || '수정 실패');
            console.error('Update project error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setActiveTab('basic');
        setProjectData(null);
        if (onCancel) onCancel();
    };

    // 배분 추가
    const handleAddAllocation = () => {
        setEditableAllocations([
            ...editableAllocations,
            {
                id: null,
                department_id: null,
                department: null,  // ⭐ null로 초기화
                allocation_rate: 0,
                allocated_amount: 0
            }
        ]);
    };

    // 배분 삭제
    const handleRemoveAllocation = (index) => {
        const newAllocations = editableAllocations.filter((_, i) => i !== index);
        setEditableAllocations(newAllocations);
    };

    // 배분율 변경
    const handleAllocationRateChange = (index, rate) => {
        const newAllocations = [...editableAllocations];
        const contractAmount = projectData?.contract_amount || 0;

        newAllocations[index].allocation_rate = rate;
        newAllocations[index].allocated_amount = (contractAmount * rate) / 100;

        setEditableAllocations(newAllocations);
    };

    // 부서 변경
    const handleDepartmentChange = (index, departmentId) => {
        const dept = departments.find(d => d.id === departmentId);
        const newAllocations = [...editableAllocations];

        newAllocations[index].department_id = departmentId;
        newAllocations[index].department = dept;  // ⭐ 객체 전체 저장

        setEditableAllocations(newAllocations);
    };

    // 배분율 합계 계산
    const getTotalAllocationRate = () => {
        return editableAllocations.reduce((sum, a) => sum + (parseFloat(a.allocation_rate) || 0), 0);
    };

    // 배분 저장
    const handleSaveAllocations = async () => {
        const totalRate = getTotalAllocationRate();

        if (Math.abs(totalRate - 100) > 0.01) {
            message.error(`배분율 합계가 100%가 아닙니다 (현재: ${totalRate.toFixed(1)}%)`);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            
            await axios.put(
                `${API_BASE}/projects/${projectId}/allocations`,
                {
                    department_allocations: editableAllocations.map(a => ({
                        id: a.id,
                        department_id: a.department_id,
                        allocation_rate: a.allocation_rate,
                        allocated_amount: a.allocated_amount
                    })),
                    contract_amount: projectData?.contract_amount || 0
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setAllocationEditing(false);
            message.success('매출 귀속 부서가 저장되었습니다');
            
            // 프로젝트 데이터 새로고침
            await loadProjectData();
        } catch (error) {
            message.error(error.response?.data?.message || '저장 실패');
            console.error('Save allocations error:', error);
        }
    };

    // 배분 편집 취소
    const handleCancelAllocations = () => {
        setEditableAllocations(allocations.map(a => ({
            id: a.id,
            department_id: a.department_id,
            department: a.department,  // ⭐ 객체 전체 복사
            allocation_rate: a.allocation_rate,
            allocated_amount: a.allocated_amount
        })));
        setAllocationEditing(false);
    };

    // 예산 변경
    const handleBudgetChange = (index, field, value) => {
        const newBudgets = [...editableBudgets];
        newBudgets[index][field] = value || 0;
        setEditableBudgets(newBudgets);
    };

    // 예산 합계 계산
    const getBudgetTotal = (budget) => {
        return (
            (parseFloat(budget.labor_budget) || 0) +
            (parseFloat(budget.outsourcing_budget) || 0) +
            (parseFloat(budget.expense_budget) || 0) +
            (parseFloat(budget.other_budget) || 0)
        );
    };

    // 예산 검증
    const validateBudget = (budget) => {
        const total = getBudgetTotal(budget);
        const revenueBudget = parseFloat(budget.revenue_budget) || 0;
        return total <= revenueBudget;
    };

    // 예산 저장
    const handleSaveBudgets = async () => {
        // 모든 예산이 매출예산을 초과하지 않는지 검증
        for (const budget of editableBudgets) {
            if (!validateBudget(budget)) {
                const total = getBudgetTotal(budget);
                message.error(
                    `${budget.department?.name} 부서의 예산 합계(${Math.round(total).toLocaleString()}원)가 ` +
                    `매출예산(${Math.round(budget.revenue_budget).toLocaleString()}원)을 초과합니다`
                );
                return;
            }
        }

        try {
            const token = localStorage.getItem('token');
            
            await axios.put(
                `${API_BASE}/projects/${projectId}/budgets`,
                {
                    budgets: editableBudgets.map(b => ({
                        id: b.id,
                        department_id: b.department_id,
                        revenue_budget: Math.round(b.revenue_budget || 0),
                        labor_budget: Math.round(b.labor_budget || 0),
                        outsourcing_budget: Math.round(b.outsourcing_budget || 0),
                        expense_budget: Math.round(b.expense_budget || 0),
                        other_budget: Math.round(b.other_budget || 0)
                    }))
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setBudgetEditing(false);
            message.success('예산편성이 저장되었습니다');
            
            // 프로젝트 데이터 새로고침
            await loadProjectData();
        } catch (error) {
            message.error(error.response?.data?.message || '저장 실패');
            console.error('Save budgets error:', error);
        }
    };

    // 예산 편집 시작
    const handleStartBudgetEditing = () => {
        // 기존 예산이 있으면 복사, 없으면 매출 귀속 부서별로 기본 예산 생성
        if (budgets && budgets.length > 0) {
            setEditableBudgets(budgets.map(b => ({ ...b })));
        } else if (allocations && allocations.length > 0) {
            // 매출 귀속 부서별로 빈 예산 항목 생성
            const defaultBudgets = allocations.map(allocation => ({
                id: null,
                department_id: allocation.department_id,
                department: allocation.department,
                revenue_budget: Math.round(allocation.allocated_amount || 0),
                labor_budget: 0,
                outsourcing_budget: 0,
                expense_budget: 0,
                other_budget: 0
            }));
            setEditableBudgets(defaultBudgets);
        } else {
            message.warning('먼저 매출 귀속 부서를 설정해주세요');
            return;
        }
        setBudgetEditing(true);
    };

    // 예산 편집 취소
    const handleCancelBudgets = () => {
        setEditableBudgets(budgets.map(b => ({ ...b })));
        setBudgetEditing(false);
    };

    // ⭐ 인력 배정 관련 함수들
    const handleAddMember = () => {
        memberForm.resetFields();
        setEstimatedLaborCost(0);
        setMemberModalVisible(true);
    };

    const handleSubmitMember = async () => {
        try {
            const values = await memberForm.validateFields();
            const token = localStorage.getItem('token');
            
            const submitData = {
                ...values,
                estimated_labor_cost: estimatedLaborCost
            };
            
            await axios.post(`${API_BASE}/project-members/project/${projectId}`, submitData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            message.success('인력이 배정되었습니다');
            setMemberModalVisible(false);
            memberForm.resetFields();
            loadProjectData(); // 데이터 새로고침
        } catch (error) {
            message.error(error.response?.data?.message || '인력 배정 실패');
        }
    };

    const handleRemoveMember = async (memberId) => {
        try {
            const token = localStorage.getItem('token');
            
            console.log('삭제 요청:', `${API_BASE}/project-members/${memberId}`);
            
            const response = await axios.delete(`${API_BASE}/project-members/${memberId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            console.log('삭제 응답:', response.data);
            message.success('인력이 해제되었습니다');
            
            // 데이터 새로고침
            console.log('데이터 새로고침 시작...');
            await loadProjectData();
            console.log('데이터 새로고침 완료'); 
        } catch (error) {
            console.error('삭제 에러:', error);
            message.error(error.response?.data?.message || '인력 해제 실패');
        }
    };

    // 인력 수정 기능
    const [editingMember, setEditingMember] = useState(null);
    const [memberEditModalVisible, setMemberEditModalVisible] = useState(false);
    const [memberEditForm] = Form.useForm();
    const [editEstimatedLaborCost, setEditEstimatedLaborCost] = useState(0);

    const handleEditMember = (member) => {
        setEditingMember(member);
        memberEditForm.setFieldsValue({
            budget_department_id: member.budget_department_id,
            role: member.role,
            person_count: member.person_count,
            work_days: member.work_days,
            participation_rate: member.participation_rate,
            notes: member.notes
        });
        setEditEstimatedLaborCost(member.estimated_labor_cost || 0);
        setMemberEditModalVisible(true);
    };

    // 수정 폼에서 예상 인건비 계산
    const updateEditEstimatedCost = useCallback(() => {
        const values = memberEditForm.getFieldsValue();
        const { budget_department_id, person_count, work_days, participation_rate } = values;
        
        if (budget_department_id && person_count && work_days && participation_rate) {
            const cost = calculateEstimatedLaborCost(
                budget_department_id, 
                person_count, 
                work_days, 
                participation_rate
            );
            setEditEstimatedLaborCost(cost);
        } else {
            setEditEstimatedLaborCost(0);
        }
    }, [memberEditForm, calculateEstimatedLaborCost]);

    const handleUpdateMember = async () => {
        try {
            const values = await memberEditForm.validateFields();
            const token = localStorage.getItem('token');
            
            const updateData = {
                ...values,
                estimated_labor_cost: editEstimatedLaborCost
            };
            
            await axios.put(`${API_BASE}/project-members/${editingMember.id}`, updateData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            message.success('인력 정보가 수정되었습니다');
            setMemberEditModalVisible(false);
            setEditingMember(null);
            setEditEstimatedLaborCost(0);
            memberEditForm.resetFields();
            loadProjectData(); // 데이터 새로고침
        } catch (error) {
            message.error(error.response?.data?.message || '인력 정보 수정 실패');
        }
    };

    const confirmRemoveMember = (member) => {
        Modal.confirm({
            title: '인력 해제',
            content: `${member.role} 역할의 인력을 프로젝트에서 해제하시겠습니까?`,
            onOk: () => handleRemoveMember(member.id),
            okText: '해제',
            cancelText: '취소',
            okType: 'danger',
        });
    };


    const memberColumns = [
        {
            title: '이름',
            dataIndex: ['user', 'name'],
            key: 'name',
            width: 100,
            render: (name, record) => (
                <div>
                    <div><Text strong>{name}</Text></div>
                    <div><Text type="secondary" style={{ fontSize: 12 }}>{record.user?.employee_id}</Text></div>
                </div>
            )
        },
        {
            title: '역할',
            dataIndex: 'role',
            key: 'role',
            width: 100,
        },
        {
            title: '귀속부서',
            dataIndex: ['budget_department', 'name'],
            key: 'budget_department',
            width: 120,
            render: (name, record) => (
                <Tag color="blue">{record.budget_department?.name}</Tag>
            )
        },
        {
            title: '투입 인원수',
            dataIndex: 'person_count',
            key: 'person_count',
            width: 80,
            align: 'right',
            render: (count) => `${count || 1}명`
        },
        {
            title: '작업 기간',
            dataIndex: 'work_days',
            key: 'work_days',
            width: 80,
            align: 'right',
            render: (days) => `${days || 1}일`
        },
        {
            title: '참여율',
            dataIndex: 'participation_rate',
            key: 'participation_rate',
            width: 80,
            align: 'right',
            render: (rate) => `${rate || 100}%`
        },
        {
            title: '집행률',
            dataIndex: 'execution_rate',
            key: 'execution_rate',
            width: 80,
            align: 'right',
            render: (rate) => `${rate || 0}%`
        },
        {
            title: '예상 인건비',
            dataIndex: 'estimated_labor_cost',
            key: 'estimated_labor_cost',
            width: 120,
            align: 'right',
            render: (cost) => (
                <Text strong style={{ color: '#52c41a' }}>
                    {Math.round(cost || 0).toLocaleString()}원
                </Text>
            )
        }
    ];

    const tabItems = [
        {
            key: 'basic',
            label: '기본 정보',
            children: (
                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="code"
                                label="프로젝트 코드"
                            >
                                <Input disabled />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label="프로젝트명"
                                rules={[{ required: true, message: '프로젝트명을 입력하세요' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* ⭐ PM, 기술리더, 대표부서 - 수정 가능 */}
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item 
                                name="pm_id"
                                label="프로젝트 PM"
                            >
                                <Select
                                    placeholder="PM 선택"
                                    allowClear
                                    showSearch
                                    loading={users.length === 0}
                                    disabled={users.length === 0}
                                    filterOption={(input, option) =>
                                        option.label.toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={users.map((user) => ({
                                        label: `${user.name} (${user.employee_id}) - ${user.department?.name || '부서없음'}`,
                                        value: user.id,
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item 
                                name="tech_leader_id"
                                label="기술 리더"
                            >
                                <Select
                                    placeholder="기술 리더 선택"
                                    allowClear
                                    showSearch
                                    loading={users.length === 0}
                                    disabled={users.length === 0}
                                    filterOption={(input, option) =>
                                        option.label.toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={users.map((user) => ({
                                        label: `${user.name} (${user.employee_id}) - ${user.department?.name || '부서없음'}`,
                                        value: user.id,
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item 
                                name="primary_department_id"
                                label="대표 부서"
                                rules={[{ required: true, message: '대표 부서를 선택하세요' }]}
                            >
                                <Select
                                    placeholder="대표 부서 선택"
                                    showSearch
                                    loading={departments.length === 0}
                                    disabled={departments.length === 0}
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option?.children?.toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={(departmentId) => {
                                        // 대표부서 변경 시 해당 부서 인력만 로드
                                        loadUsers(departmentId);
                                        
                                        // PM과 기술리더 선택 초기화 (부서가 변경되었으므로)
                                        form.setFieldsValue({
                                            pm_id: null,
                                            tech_leader_id: null
                                        });
                                    }}
                                >
                                    {departments.map(dept => (
                                        <Option key={dept.id} value={dept.id}>
                                            {dept.name} ({dept.code})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="status"
                                label="프로젝트 상태"
                                rules={[{ required: true, message: '상태를 선택하세요' }]}
                            >
                                <Select>
                                    <Option value="planning">계획</Option>
                                    <Option value="in_progress">진행중</Option>
                                    <Option value="on_hold">보류</Option>
                                    <Option value="completed">완료</Option>
                                    <Option value="cancelled">취소</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="progress" label="진행률 (%)">
                                <InputNumber min={0} max={100} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="contract_date" label="계약일">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="start_date" label="시작일">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="end_date" label="종료일">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="contract_amount"
                                label="계약금액 (원)"
                                rules={[{ required: true, message: '계약금액을 입력하세요' }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="location" label="프로젝트 위치">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="client_name" label="고객사">
                        <Input />
                    </Form.Item>

                    <Form.Item name="client_contact" label="고객 담당자">
                        <Input />
                    </Form.Item>

                    <Form.Item name="description" label="프로젝트 설명">
                        <TextArea rows={4} />
                    </Form.Item>

                    <Form.Item 
                        name="flow_link" 
                        label="공정관리 플로우 링크"
                        rules={[
                            { 
                                type: 'url', 
                                message: '올바른 URL 형식을 입력하세요' 
                            }
                        ]}
                    >
                        <Space.Compact style={{ width: '100%' }}>
                            <Input 
                                style={{ width: '50px', textAlign: 'center' }}
                                value="🔗" 
                                disabled 
                            />
                            <Input 
                                placeholder="https://flow.example.com/project/..." 
                                style={{ width: 'calc(100% - 50px)' }}
                            />
                        </Space.Compact>
                    </Form.Item>
                </Form>
            )
        },
        {
            key: 'allocations',
            label: '매출 귀속 부서',
            children: (
                <div>
                    {/* 메인 부서 (변경 불가) */}
                    <Card
                        size="small"
                        title="대표 부서 (변경 불가)"
                        style={{ marginBottom: 16 }}
                    >
                        <Row gutter={16}>
                            <Col span={8}>
                                <Text strong>부서명: </Text>
                                <Text>{projectData?.primary_department?.name || '-'}</Text>
                            </Col>
                            <Col span={8}>
                                <Text strong>배분율: </Text>
                                <Text>고정 (하위 배분에서 조정)</Text>
                            </Col>
                            <Col span={8}>
                                <Text strong>계약금액: </Text>
                                <Text>{Math.round(projectData?.contract_amount || 0).toLocaleString()}원</Text>
                            </Col>
                        </Row>
                    </Card>

                    {/* 하위 배분 */}
                    <Card
                        size="small"
                        title="부서별 매출 배분"
                        extra={
                            allocationEditing ? (
                                <Space>
                                    <Button
                                        size="small"
                                        onClick={handleCancelAllocations}
                                    >
                                        취소
                                    </Button>
                                    <Button
                                        type="primary"
                                        size="small"
                                        onClick={handleSaveAllocations}
                                    >
                                        저장
                                    </Button>
                                </Space>
                            ) : (
                                <Button
                                    type="primary"
                                    size="small"
                                    onClick={() => setAllocationEditing(true)}
                                >
                                    편집
                                </Button>
                            )
                        }
                    >
                        {allocationEditing ? (
                            <div>
                                <Table
                                    dataSource={editableAllocations}
                                    rowKey={(record, index) => index}
                                    pagination={false}
                                    size="small"
                                    columns={[
                                        {
                                            title: '부서',
                                            dataIndex: 'department_id',
                                            key: 'department',
                                            width: 200,
                                            render: (deptId, record, index) => (
                                                <Select
                                                    value={deptId}
                                                    onChange={(value) => handleDepartmentChange(index, value)}
                                                    style={{ width: '100%' }}
                                                    placeholder="부서 선택"
                                                >
                                                    {departments.map(dept => (
                                                        <Option key={dept.id} value={dept.id}>
                                                            {dept.name}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            )
                                        },
                                        {
                                            title: '배분율 (%)',
                                            dataIndex: 'allocation_rate',
                                            key: 'rate',
                                            width: 150,
                                            align: 'center',
                                            render: (rate, record, index) => (
                                                <InputNumber
                                                    value={rate}
                                                    onChange={(value) => handleAllocationRateChange(index, value)}
                                                    min={0}
                                                    max={100}
                                                    precision={1}
                                                    style={{ width: '100%' }}
                                                />
                                            )
                                        },
                                        {
                                            title: '배분금액',
                                            dataIndex: 'allocated_amount',
                                            key: 'amount',
                                            align: 'right',
                                            render: (amount) => `${Math.round(amount || 0).toLocaleString()}원`
                                        },
                                        {
                                            title: '작업',
                                            key: 'action',
                                            width: 80,
                                            align: 'center',
                                            render: (_, record, index) => (
                                                <Button
                                                    type="link"
                                                    danger
                                                    size="small"
                                                    onClick={() => handleRemoveAllocation(index)}
                                                >
                                                    삭제
                                                </Button>
                                            )
                                        }
                                    ]}
                                    footer={() => {
                                        const totalRate = getTotalAllocationRate();
                                        const isValid = Math.abs(totalRate - 100) < 0.01;

                                        return (
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}>
                                                <Button
                                                    type="dashed"
                                                    onClick={handleAddAllocation}
                                                    icon={<PlusOutlined />}
                                                >
                                                    부서 추가
                                                </Button>
                                                <div>
                                                    <Text strong>합계: </Text>
                                                    <Text
                                                        strong
                                                        style={{
                                                            color: isValid ? '#52c41a' : '#ff4d4f',
                                                            fontSize: 16
                                                        }}
                                                    >
                                                        {totalRate.toFixed(1)}%
                                                    </Text>
                                                    {!isValid && (
                                                        <Text type="danger" style={{ marginLeft: 8 }}>
                                                            (100%가 되어야 합니다)
                                                        </Text>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }}
                                />
                            </div>
                        ) : (
                            <Table
                                dataSource={allocations}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                columns={[
                                    {
                                        title: '부서',
                                        dataIndex: ['department', 'name'],
                                        key: 'department'
                                    },
                                    {
                                        title: '배분율',
                                        dataIndex: 'allocation_rate',
                                        key: 'rate',
                                        align: 'center',
                                        render: (rate) => `${rate}%`
                                    },
                                    {
                                        title: '배분금액',
                                        dataIndex: 'allocated_amount',
                                        key: 'amount',
                                        align: 'right',
                                        render: (amount) => `${Math.round(amount || 0).toLocaleString()}원`
                                    }
                                ]}
                                summary={() => {
                                    const totalRate = allocations.reduce(
                                        (sum, a) => sum + (parseFloat(a.allocation_rate) || 0),
                                        0
                                    );
                                    const totalAmount = allocations.reduce(
                                        (sum, a) => sum + (parseFloat(a.allocated_amount) || 0),
                                        0
                                    );

                                    return (
                                        <Table.Summary.Row style={{ background: '#fafafa' }}>
                                            <Table.Summary.Cell>
                                                <Text strong>합계</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell align="center">
                                                <Text strong>{totalRate.toFixed(1)}%</Text>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell align="right">
                                                <Text strong>{Math.round(totalAmount).toLocaleString()}원</Text>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    );
                                }}
                            />
                        )}
                    </Card>
                </div>
            )
        },
        {
            key: 'budgets',
            label: '예산 편성',
            children: projectData ? (
                <BudgetPlanningTab projectId={projectId} projectData={projectData} onDataChange={loadProjectData} />
            ) : (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Text type="secondary">데이터를 불러오는 중...</Text>
                </div>
            )
        },
        {
            key: 'members',
            label: '투입 인력 현황',
            children: projectData ? (
                <div>
                    {/* 인력 관리 헤더 */}
                    <Card size="small" style={{ marginBottom: 16 }}>
                        <Row justify="space-between" align="middle">
                            <Col>
                                <Space>
                                    <UserAddOutlined style={{ color: '#1890ff' }} />
                                    <span style={{ fontSize: 16, fontWeight: 500 }}>프로젝트 인력 관리</span>
                                </Space>
                            </Col>
                            <Col>
                                <Button 
                                    type="primary" 
                                    icon={<PlusOutlined />}
                                    onClick={handleAddMember}
                                >
                                    인력 배정
                                </Button>
                            </Col>
                        </Row>
                    </Card>

                    {/* 인건비 요약 */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={6}>
                            <Card size="small">
                                <Statistic
                                    title="총 인건비 예산"
                                    value={projectData.members?.reduce((sum, member) => sum + (parseInt(member.estimated_labor_cost) || 0), 0) || 0}
                                    suffix="원"
                                    styles={{ content: { fontSize: 18 } }}
                                    formatter={(value) => value.toLocaleString()}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card size="small">
                                <Statistic
                                    title="지출 인건비"
                                    value={projectData.members?.reduce((sum, member) => {
                                        const estimatedCost = parseInt(member.estimated_labor_cost) || 0;
                                        const executionRate = parseFloat(member.execution_rate) || 0;
                                        return sum + (estimatedCost * executionRate / 100);
                                    }, 0) || 0}
                                    suffix="원"
                                    styles={{ content: { fontSize: 18, color: '#1890ff' } }}
                                    formatter={(value) => Math.round(value).toLocaleString()}
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card size="small">
                                <Statistic
                                    title="잔여 인건비"
                                    value={(() => {
                                        const totalBudget = projectData.members?.reduce((sum, member) => sum + (parseInt(member.estimated_labor_cost) || 0), 0) || 0;
                                        const spentBudget = projectData.members?.reduce((sum, member) => {
                                            const estimatedCost = parseInt(member.estimated_labor_cost) || 0;
                                            const executionRate = parseFloat(member.execution_rate) || 0;
                                            return sum + (estimatedCost * executionRate / 100);
                                        }, 0) || 0;
                                        return totalBudget - spentBudget;
                                    })()}
                                    suffix="원"
                                    styles={{ 
                                        content: { 
                                            fontSize: 18, 
                                            color: (() => {
                                                const totalBudget = projectData.members?.reduce((sum, member) => sum + (parseInt(member.estimated_labor_cost) || 0), 0) || 0;
                                                const spentBudget = projectData.members?.reduce((sum, member) => {
                                                    const estimatedCost = parseInt(member.estimated_labor_cost) || 0;
                                                    const executionRate = parseFloat(member.execution_rate) || 0;
                                                    return sum + (estimatedCost * executionRate / 100);
                                                }, 0) || 0;
                                                return (totalBudget - spentBudget) >= 0 ? '#52c41a' : '#ff4d4f';
                                            })()
                                        } 
                                    }}
                                    formatter={(value) => Math.round(value).toLocaleString()}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* 인력 배정 리스트 */}
                    <Card title="인력 배정 현황">
                        <Table
                            dataSource={projectData.members || []}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: '귀속부서',
                                    dataIndex: ['budget_department', 'name'],
                                    key: 'department',
                                    width: 120,
                                    render: (name, record) => (
                                        <Tag color="blue">{record.budget_department?.name}</Tag>
                                    )
                                },
                                {
                                    title: '역할',
                                    dataIndex: 'role',
                                    key: 'role',
                                    width: 120,
                                },
                                {
                                    title: '투입 인원수',
                                    dataIndex: 'person_count',
                                    key: 'person_count',
                                    width: 100,
                                    align: 'center',
                                    render: (count) => `${count || 1}명`
                                },
                                {
                                    title: '참여 기간',
                                    dataIndex: 'work_days',
                                    key: 'work_days',
                                    width: 100,
                                    align: 'center',
                                    render: (days) => `${days || 1}일`
                                },
                                {
                                    title: '참여율',
                                    dataIndex: 'participation_rate',
                                    key: 'participation_rate',
                                    width: 80,
                                    align: 'center',
                                    render: (rate) => `${rate || 100}%`
                                },
                                {
                                    title: '집행률',
                                    dataIndex: 'execution_rate',
                                    key: 'execution_rate',
                                    width: 80,
                                    align: 'center',
                                    render: (rate) => `${rate || 0}%`
                                },
                                {
                                    title: '예상 인건비',
                                    dataIndex: 'estimated_labor_cost',
                                    key: 'estimated_labor_cost',
                                    width: 120,
                                    align: 'right',
                                    render: (cost) => (
                                        <Text strong style={{ color: '#52c41a' }}>
                                            {Math.round(cost || 0).toLocaleString()}원
                                        </Text>
                                    )
                                },
                                {
                                    title: '작업',
                                    key: 'action',
                                    width: 120,
                                    align: 'center',
                                    render: (_, record) => (
                                        <Space>
                                            <Button
                                                type="text"
                                                icon={<EditOutlined />}
                                                size="small"
                                                onClick={() => handleEditMember(record)}
                                                title="수정"
                                            />
                                            <Button
                                                type="text"
                                                danger
                                                icon={<UserDeleteOutlined />}
                                                size="small"
                                                onClick={() => confirmRemoveMember(record)}
                                                title="삭제"
                                            />
                                        </Space>
                                    )
                                }
                            ]}
                            summary={(pageData) => {
                                const totalPersonCount = pageData.reduce((sum, item) => sum + (parseInt(item.person_count) || 0), 0);
                                const totalWorkDays = pageData.reduce((sum, item) => sum + (parseInt(item.work_days) || 0), 0);
                                const totalEstimatedCost = pageData.reduce((sum, item) => sum + (parseInt(item.estimated_labor_cost) || 0), 0);

                                return (
                                    <Table.Summary.Row style={{ background: '#fafafa' }}>
                                        <Table.Summary.Cell></Table.Summary.Cell>
                                        <Table.Summary.Cell>
                                            <Text strong>총 합계</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell align="center">
                                            <Text strong>{totalPersonCount}명</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell align="center">
                                            <Text strong>{totalWorkDays}일</Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell></Table.Summary.Cell>
                                        <Table.Summary.Cell></Table.Summary.Cell>
                                        <Table.Summary.Cell align="right">
                                            <Text strong style={{ color: '#52c41a' }}>
                                                {Math.round(totalEstimatedCost).toLocaleString()}원
                                            </Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell></Table.Summary.Cell>
                                    </Table.Summary.Row>
                                );
                            }}
                        />
                    </Card>
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Text type="secondary">데이터를 불러오는 중...</Text>
                </div>
            )
        }
    ];

    return (
        <Modal
            title="프로젝트 수정"
            open={visible}
            onCancel={handleCancel}
            onOk={handleSubmit}
            confirmLoading={loading}
            width={1200}
            okText="수정"
            cancelText="취소"
        >
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
            />
            
            {/* 인력 배정 모달 */}
            <Modal
                title="인력 배정"
                open={memberModalVisible}
                onOk={handleSubmitMember}
                onCancel={() => {
                    setMemberModalVisible(false);
                    memberForm.resetFields();
                }}
                okText="배정"
                cancelText="취소"
                width={600}
            >
                <div style={{ 
                    padding: '12px', 
                    backgroundColor: '#e6f7ff', 
                    border: '1px solid #91d5ff',
                    borderRadius: '6px',
                    marginBottom: '16px'
                }}>
                    <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                        📋 인력 배정 안내
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        • 역할, 투입인원수, 참여기간, 참여율을 입력해 주세요<br/>
                        • 부서별 1인당 1일 평균 인건비로 예삵 인건비가 자동 계산됩니다<br/>
                        • 매출 귀속 부서: {projectData?.allocations?.map(a => a.department?.name).join(', ') || '없음'}
                    </div>
                </div>
                
                <Form
                    form={memberForm}
                    layout="vertical"
                    initialValues={{
                        person_count: 1,
                        work_days: 1,
                        participation_rate: 100
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="budget_department_id"
                                label="귀속 부서"
                                rules={[{ required: true, message: '귀속 부서를 선택하세요' }]}
                            >
                                <Select
                                    placeholder="부서 선택"
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option?.children?.toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={updateEstimatedCost}
                                >
                                    {(projectData?.allocations || []).map(allocation => (
                                        <Option key={allocation.department_id} value={allocation.department_id}>
                                            {allocation.department?.name} ({allocation.department?.code})
                                            <br/>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                1일 인건비: {Math.round(departmentBudgets[allocation.department_id]?.dailyLaborCostPerPerson || 0).toLocaleString()}원
                                            </Text>
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="role"
                                label="역할"
                                rules={[{ required: true, message: '역할을 입력하세요' }]}
                            >
                                <Select placeholder="역할 선택">
                                    <Option value="PM">프로젝트 매니저</Option>
                                    <Option value="기술리더">기술 리더</Option>
                                    <Option value="선임개발자">선임 개발자</Option>
                                    <Option value="개발자">개발자</Option>
                                    <Option value="주니어개발자">주니어 개발자</Option>
                                    <Option value="디자이너">디자이너</Option>
                                    <Option value="QA">QA</Option>
                                    <Option value="기타">기타</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="person_count"
                                label="투입 인원수 (명)"
                                rules={[{ required: true, message: '투입 인원수를 입력하세요' }]}
                            >
                                <InputNumber
                                    min={1}
                                    style={{ width: '100%' }}
                                    onChange={updateEstimatedCost}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="work_days"
                                label="참여 기간 (일)"
                                rules={[{ required: true, message: '참여 기간을 입력하세요' }]}
                            >
                                <InputNumber
                                    min={1}
                                    style={{ width: '100%' }}
                                    onChange={updateEstimatedCost}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="participation_rate"
                                label="참여율 (%)"
                                rules={[{ required: true, message: '참여율을 입력하세요' }]}
                            >
                                <InputNumber
                                    min={0}
                                    max={100}
                                    precision={1}
                                    style={{ width: '100%' }}
                                    onChange={updateEstimatedCost}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="execution_rate"
                                label="집행률 (%)"
                                rules={[{ required: true, message: '집행률을 입력하세요' }]}
                                initialValue={0}
                            >
                                <InputNumber
                                    min={0}
                                    max={100}
                                    precision={1}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    
                    {/* 예상 인건비 표시 */}
                    <Row gutter={16}>
                        <Col span={24}>
                            <div style={{ 
                                padding: '16px', 
                                backgroundColor: '#f6ffed', 
                                border: '1px solid #b7eb8f',
                                borderRadius: '6px',
                                marginBottom: '16px'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text strong style={{ color: '#52c41a' }}>
                                        💰 예상 인건비: 
                                    </Text>
                                    <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                                        {estimatedLaborCost.toLocaleString()}원
                                    </Text>
                                </div>
                                {estimatedLaborCost > 0 && (
                                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                        인원수 × 기간 × 참여율 × 1인당 1일 평균 인건비로 계산
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                    
                    <Form.Item
                        name="notes"
                        label="비고"
                    >
                        <TextArea rows={3} placeholder="추가 설명이나 특이사항" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 인력 수정 모달 */}
            <Modal
                title="인력 정보 수정"
                open={memberEditModalVisible}
                onOk={handleUpdateMember}
                onCancel={() => {
                    setMemberEditModalVisible(false);
                    setEditingMember(null);
                    setEditEstimatedLaborCost(0);
                    memberEditForm.resetFields();
                }}
                okText="수정"
                cancelText="취소"
                width={600}
            >
                <Form
                    form={memberEditForm}
                    layout="vertical"
                    initialValues={{
                        person_count: 1,
                        work_days: 1,
                        participation_rate: 100
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="budget_department_id"
                                label="귀속 부서"
                                rules={[{ required: true, message: '귀속 부서를 선택하세요' }]}
                            >
                                <Select
                                    placeholder="부서 선택"
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option?.children?.toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={updateEditEstimatedCost}
                                >
                                    {(projectData?.allocations || []).map(allocation => (
                                        <Option key={allocation.department_id} value={allocation.department_id}>
                                            {allocation.department?.name} ({allocation.department?.code})
                                            <br/>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                1일 인건비: {Math.round(departmentBudgets[allocation.department_id]?.dailyLaborCostPerPerson || 0).toLocaleString()}원
                                            </Text>
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="role"
                                label="역할"
                                rules={[{ required: true, message: '역할을 입력하세요' }]}
                            >
                                <Select placeholder="역할 선택">
                                    <Option value="PM">프로젝트 매니저</Option>
                                    <Option value="기술리더">기술 리더</Option>
                                    <Option value="선임개발자">선임 개발자</Option>
                                    <Option value="개발자">개발자</Option>
                                    <Option value="주니어개발자">주니어 개발자</Option>
                                    <Option value="디자이너">디자이너</Option>
                                    <Option value="QA">QA</Option>
                                    <Option value="기타">기타</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="person_count"
                                label="투입 인원수 (명)"
                                rules={[{ required: true, message: '투입 인원수를 입력하세요' }]}
                            >
                                <InputNumber
                                    min={1}
                                    style={{ width: '100%' }}
                                    onChange={updateEditEstimatedCost}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="work_days"
                                label="참여 기간 (일)"
                                rules={[{ required: true, message: '참여 기간을 입력하세요' }]}
                            >
                                <InputNumber
                                    min={1}
                                    style={{ width: '100%' }}
                                    onChange={updateEditEstimatedCost}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="participation_rate"
                                label="참여율 (%)"
                                rules={[{ required: true, message: '참여율을 입력하세요' }]}
                            >
                                <InputNumber
                                    min={0}
                                    max={100}
                                    precision={1}
                                    style={{ width: '100%' }}
                                    onChange={updateEditEstimatedCost}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="execution_rate"
                                label="집행률 (%)"
                                rules={[{ required: true, message: '집행률을 입력하세요' }]}
                            >
                                <InputNumber
                                    min={0}
                                    max={100}
                                    precision={1}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    
                    {/* 예상 인건비 표시 */}
                    <Row gutter={16}>
                        <Col span={24}>
                            <div style={{ 
                                padding: '16px', 
                                backgroundColor: '#f6ffed', 
                                border: '1px solid #b7eb8f',
                                borderRadius: '6px',
                                marginBottom: '16px'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text strong style={{ color: '#52c41a' }}>
                                        💰 예상 인건비: 
                                    </Text>
                                    <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                                        {editEstimatedLaborCost.toLocaleString()}원
                                    </Text>
                                </div>
                                {editEstimatedLaborCost > 0 && (
                                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                        인원수 × 기간 × 참여율 × 1인당 1일 평균 인건비로 계산
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                    
                    <Form.Item
                        name="notes"
                        label="비고"
                    >
                        <TextArea rows={3} placeholder="추가 설명이나 특이사항" />
                    </Form.Item>
                </Form>
            </Modal>
        </Modal>
    );
};

export default ProjectEditModal;